/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import React from 'react';
import { decorate, observable, runInAction, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Icon, Label } from 'semantic-ui-react';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import CatalogueProductDetail from './CatalogueProductDetail';
import { getProductImportStatus } from './catalogueFilterUtils';
import { getCardTitleParts } from './catalogueTitleUtils';
import { emptyCatalogueCounts } from './catalogueCountUtils';

// Shared fallback so the card still renders (as zeros) when mounted without a counts prop.
const EMPTY_COUNTS = emptyCatalogueCounts();

// expected props
// - product (via prop) — a CatalogueProduct MST model instance
// - compactMode (via prop, optional) — half-width card layout
// - counts (via prop, optional) — catalogue counts value object, defaults to empty counts
// - countsReady (via prop, optional) — false while scEnvironmentsStore has not loaded
// - catalogueProductsStore (via injection)
// - envTypesStore (via injection)
class CatalogueProductCard extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.expanded = false;
    });
  }

  get product() {
    return this.props.product;
  }

  get catalogueProductsStore() {
    return this.props.catalogueProductsStore;
  }

  get compactMode() {
    return !!this.props.compactMode;
  }

  get counts() {
    return this.props.counts || EMPTY_COUNTS;
  }

  get countsReady() {
    return !!this.props.countsReady;
  }

  getImportStatus() {
    return getProductImportStatus(this.product, this.props.envTypesStore);
  }

  handleToggleExpand = action(() => {
    this.expanded = !this.expanded;
    if (this.expanded) {
      const product = this.product;
      const detailStore = this.catalogueProductsStore.getDetailStore(product.productId);
      const region = product.regions && product.regions.length > 0 ? product.regions[0] : undefined;
      swallowError(detailStore.load(region));
    }
  });

  render() {
    const product = this.product;
    const expanded = this.expanded;
    const importStatus = this.getImportStatus();

    const accentColors = {
      'approved': '#21ba45',
      'not-approved': '#f2711c',
      'update-available': '#f2711c',
      'not-imported': '#2563eb',
    };

    return (
      <div className="workspace-card">
        <div className="workspace-card-accent" style={{ background: accentColors[importStatus] }} />
        <div className="workspace-card-content">
          {this.renderHeader(product, expanded, importStatus)}
          {expanded && this.renderDetail()}
        </div>
      </div>
    );
  }

  renderHeader(product, expanded, importStatus) {
    const regions = product.regions || [];
    const compactMode = this.compactMode;
    const { title, secondaryName } = getCardTitleParts(product, this.props.envTypesStore);

    const statusConfig = {
      'approved': { color: 'green', text: 'Approved' },
      'not-approved': { color: 'orange', text: 'Not Approved' },
      'update-available': { color: 'orange', text: 'Update Available' },
      'not-imported': { color: 'grey', text: 'Not Imported' },
    };
    const status = statusConfig[importStatus];

    return (
      <div
        className="workspace-card-header"
        style={{ cursor: 'pointer' }}
        onClick={this.handleToggleExpand}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') this.handleToggleExpand();
        }}
      >
        <div style={{ flex: 1 }}>
          {compactMode ? (
            <div className="catalogue-card-title-stack">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Label size="mini" color={status.color}>
                  {status.text}
                </Label>
                <h3 className="workspace-card-title" style={{ margin: 0 }}>
                  {title}
                </h3>
              </div>
              {secondaryName && <span className="catalogue-card-secondary-name">{secondaryName}</span>}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Label size="mini" color={status.color}>
                {status.text}
              </Label>
              <h3 className="workspace-card-title" style={{ margin: 0 }}>
                {title}
              </h3>
              {secondaryName && <span className="catalogue-card-secondary-name">{secondaryName}</span>}
            </div>
          )}
          <div style={{ color: '#6b7280', fontSize: '0.85em', marginTop: '4px' }}>{product.productId}</div>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
            {regions.map(region => (
              <Label key={region} size="mini" basic>
                {region}
              </Label>
            ))}
          </div>
        </div>
        {this.renderCounts()}
        <Icon name={expanded ? 'chevron down' : 'chevron right'} style={{ marginLeft: '8px', color: '#6b7280' }} />
      </div>
    );
  }

  // Product-level totals. Rendered in the header in both views and in both expansion
  // states. The header itself stays the expand/collapse click target — the chips carry no
  // handlers of their own and do not stop the click from bubbling.
  renderCounts() {
    const { deployed, terminated } = this.counts.getProductTotals(this.product.productId);
    return (
      <div className="catalogue-card-counts">
        {this.renderCountChip('server', 'Deployed', deployed)}
        {this.renderCountChip('trash alternate outline', 'Terminated', terminated)}
      </div>
    );
  }

  renderCountChip(icon, label, value) {
    return (
      <Label basic size="mini" className="catalogue-count-chip">
        <Icon name={icon} />
        {label} {this.countsReady ? value : <span className="catalogue-count-placeholder">&mdash;</span>}
      </Label>
    );
  }

  renderDetail() {
    const product = this.product;
    const detailStore = this.catalogueProductsStore.getDetailStore(product.productId);
    return (
      <CatalogueProductDetail
        detailStore={detailStore}
        productId={product.productId}
        productName={product.productName}
        compactMode={this.compactMode}
        counts={this.counts}
        countsReady={this.countsReady}
      />
    );
  }
}

decorate(CatalogueProductCard, {
  expanded: observable,
});

export default inject('catalogueProductsStore', 'envTypesStore')(observer(CatalogueProductCard));
